import { useGetCampaign } from 'entities/campaign';
import type { ManagerGetCampaignModel } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { SideModal, SideModalBody, useModalsActions } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import { useTestLeads } from './hooks';
import { AddIntegrationModal } from './AddIntegrationModal';
import { EditIntegrationModal } from './EditIntegrationModal';
import { Integration } from './Integration';

interface IntegrationsDataModalProps {
  campaign: ManagerGetCampaignModel;
}

export function IntegrationsDataModal(props: IntegrationsDataModalProps) {
  const getCampaignQuery = useGetCampaign(props.campaign.id, false);
  const campaign = getCampaignQuery.data && (getCampaignQuery.data.data as ManagerGetCampaignModel);

  return (
    <SideModal
      autoTestAttributesName="crm-integration-modal"
      isLoading={getCampaignQuery.isFetching}
    >
      <IntegrationsDataModalBody campaign={campaign || props.campaign} />
    </SideModal>
  );
}

function IntegrationsDataModalBody({ campaign }: IntegrationsDataModalProps) {
  const { open } = useModalsActions();
  const { openTestLead, openedTestLeads, closeTestLead, clearAllLeads } = useTestLeads();
  const integrations = campaign.campaignIntegrations || [];

  return (
    <SideModalBody title="CRM integrations">
      <div className="mb-4">
        {!!integrations.length && (
          <div className="text-sm">You can add more integrations and then manage them from this screen.</div>
        )}
        {!integrations.length && (
          <div className="text-sm text-slate-700">Please add integrations to start the setup.</div>
        )}
      </div>
      <Button
        className="w-full"
        onClick={() => {
          clearAllLeads();
          open(<AddIntegrationModal campaignId={campaign.id} />);
        }}
        variant={integrations.length ? 'outline' : 'primary'}
        {...getDataAutoTestAttributes(['button-manager-crm-integrations'])}
      >
        Add integration
      </Button>
      <div className="my-8 border-t border-slate-100" />
      {integrations.map((item, index) => (
        <Integration
          index={index}
          key={item.id}
          campaignId={campaign.id}
          value={item}
          isOpenedTestLead={openedTestLeads[item.label]}
          openTestLead={openTestLead}
          closeTestLead={closeTestLead}
          onIntegrationClick={(id) => {
            clearAllLeads();
            open(
              <EditIntegrationModal
                campaignId={campaign.id}
                integrationId={id}
              />
            );
          }}
        />
      ))}
    </SideModalBody>
  );
}
